import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faExchangeAlt, faCreditCard, faLock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const DemoSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { id: 0, title: 'Connect Wallet', icon: faWallet, color: '#3B82F6' },
    { id: 1, title: 'Select Crypto', icon: faExchangeAlt, color: '#10B981' },
    { id: 2, title: 'Convert to Fiat', icon: faCreditCard, color: '#F59E0B' },
    { id: 3, title: 'Choose Payment', icon: faLock, color: '#3B82F6' },
    { id: 4, title: 'Complete', icon: faCheckCircle, color: '#10B981' }
  ];

  const FlowchartNode = ({ step, index }) => (
    <motion.div
      className="relative group cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.2 }}
      onClick={() => setActiveStep(step.id)}
    >
      <motion.div
        className={`w-16 h-16 rounded-full flex items-center justify-center ${
          activeStep === step.id ? 'scale-110' : ''
        } transition-transform duration-300`}
        style={{ backgroundColor: step.color }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <FontAwesomeIcon icon={step.icon} className="text-white text-2xl" />
      </motion.div>
      <motion.div
        className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-sm font-medium"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.2 + 0.2 }}
      >
        {step.title}
      </motion.div>
    </motion.div>
  );

  const FlowchartConnector = ({ index }) => (
    <motion.div
      className="flex-1 h-0.5 mx-2"
      style={{ backgroundColor: steps[index].color }}
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ delay: index * 0.2, duration: 0.5 }}
    />
  );

  const WorkflowDetails = [
    {
      title: "Connect Your Wallet",
      description: "Securely connect your Web3 wallet (MetaMask, WalletConnect) to start the bridging process.",
      details: ["Support for multiple wallet providers", "Secure connection protocol", "Real-time balance updates"]
    },
    {
      title: "Select Your Crypto",
      description: "Choose which cryptocurrency you want to convert and spend.",
      details: ["Support for major cryptocurrencies", "Real-time exchange rates", "Gas fee estimation"]
    },
    {
      title: "Instant Conversion",
      description: "Your crypto is automatically converted to your preferred fiat currency.",
      details: ["Best rate aggregation", "Minimal slippage", "Transparent fee structure"]
    },
    {
      title: "Payment Method",
      description: "Choose your preferred Web2 payment method.",
      details: ["PayPal integration", "Credit/Debit cards", "Bank transfers", "Mobile payments"]
    },
    {
      title: "Transaction Complete",
      description: "Your payment is processed and confirmed instantly.",
      details: ["Transaction receipts", "Payment confirmation", "Transaction history"]
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-4xl font-bold text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          How UniPay Works
        </motion.h2>

        {/* Flowchart */}
        <div className="relative max-w-4xl mx-auto mb-20">
          <div className="flex items-center justify-center space-x-4 mb-20">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <FlowchartNode step={step} index={index} />
                {index < steps.length - 1 && <FlowchartConnector index={index} />}
              </React.Fragment>
            ))}
          </div>

          {/* Active Step Details */}
          <motion.div
            className="bg-white rounded-xl p-8 shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: steps[activeStep].color }}>
              {WorkflowDetails[activeStep].title}
            </h3>
            <p className="text-gray-600 mb-6">
              {WorkflowDetails[activeStep].description}
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {WorkflowDetails[activeStep].details.map((detail, index) => (
                <motion.li
                  key={index}
                  className="flex items-center space-x-2 text-sm text-gray-500"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                  <span>{detail}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
